import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { BookingsService } from "@/api/services/BookingsService";
import { GeolocationService } from "@/api/services/GeolocationService";
import { UserService } from "@/api/services/UserService";
import { apiScope, msalInstance, msalReady } from "@/lib/msal-config";
import { IApiError } from "@/lib/types";
import { ProjectService } from "./ProjectService";

// ---------------------- Token Acquisition

async function getAccessToken(): Promise<string> {
  await msalReady;
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error("No authenticated account");
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [apiScope],
      account: accounts[0],
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.loginRedirect({ scopes: [apiScope] });
      throw error;
    }
    throw error;
  }
}

// ---------------------- Service

export class ApiService {
  // --------- Variables

  static user = UserService;
  static bookings = BookingsService;
  static geolocation = GeolocationService;
  static projects = ProjectService;

  // --------- Methods

  static async sendRequest(endpoint: string, options: RequestInit = {}) {
    const headers = new Headers(options.headers);

    try {
      const token = await getAccessToken();
      headers.set("Authorization", `Bearer ${token}`);

      // Add default content type if it's not a GET request and no content-type is set
      if (options.method && options.method !== "GET" && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }

      // Send the request
      const response = await fetch(endpoint, {
        ...options,
        headers,
      });

      // Got some error response
      if (!response.ok) {
        const contentType = response.headers.get("Content-Type");

        let errorData: any = null;

        // Let's try to get the error response data

        if (contentType?.includes("application/problem+json")) {
          // Problem Details Response
          errorData = await response.json();
        } else if (contentType?.includes("application/json")) {
          // Not Problem Details but some other JSON response
          try {
            errorData = await response.json();
          } catch {
            errorData = null;
          }
        } else {
          // Some other error response
          try {
            errorData = await response.text();
          } catch {
            errorData = null;
          }
        }

        // Let's make a standard format error
        const error = this.createApiError({
          status: response.status,
          statusText: response.statusText,
          url: endpoint,
          method: options.method || "GET",
          data: errorData,
        });

        // Unauthorized Error
        if (response.status === 401) {
          await msalInstance.loginRedirect({ scopes: [apiScope] });
        }

        if (errorData && typeof errorData === "object" && "title" in errorData) {
          // Problem Details Error
          error.problemDetails = errorData;
          error.message =
            errorData.detail || errorData.title || `Request failed: ${response.statusText}`;
        } else if (typeof errorData === "string") {
          // Some other error string
          error.message = errorData;
        } else {
          // Something else
          error.message = errorData?.message || `Request failed: ${response.statusText}`;
        }

        throw error;
      }

      // If we got here, we didn't get an error

      if (response.status === 204) {
        return;
      } else {
        return await response.json();
      }
    } catch (error) {
      // Some other error happened...pass it along
      if (error instanceof Error && "status" in error) {
        throw error;
      }

      // Some low-level network error occurred
      throw this.createApiError({
        status: 0,
        statusText: "Network Error",
        url: endpoint,
        method: options.method || "GET",
        data: error,
        message: error instanceof Error ? error.message : "An unexpected error occurred",
      });
    }
  }

  private static createApiError({
    status,
    statusText,
    url,
    method,
    data = null,
    message = "",
  }: Partial<IApiError> & {
    status: number;
    statusText: string;
    url: string;
    method: string;
  }): IApiError {
    const error = new Error(message) as IApiError;
    error.status = status;
    error.statusText = statusText;
    error.url = url;
    error.method = method;
    error.data = data;
    return error;
  }
}
